/**
 * @file `Settings` — namespacer for `admin.settings.*` corresponding
 *       to the `/settings` panel in the admin shell.
 * @module @labcraft/admin/src/client/settings
 *
 * One `CoreLifecycle`-based namespacer per UI panel (per the workspace
 * Client-shape rule). Owns no transport of its own — the parent
 * `Admin` adopts the shared `HttpTransport` and hands a reference here
 * at construction. Logs and metrics bubble under `admin.settings.*`.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { HttpTransport } from './index.ts'
import {
  SetSettingRoute,
  SettingsHistoryRoute,
  SettingsSchemaRoute,
  SettingsValuesRoute,
  type SetSettingResponse,
  type SettingsHistoryResponse,
  type SettingsSchemaResponse,
  type SettingsValuesResponse,
} from '../shared/settings_routes.ts'

/**
 * Typed namespacer for the admin `/api/settings/*` surface. Held on
 * the frontend `Admin` Client; React components reach it via
 * `useClient<Admin>().settings`.
 */
export class Settings extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'settings' })
    this.http = http
  }

  /** Per-daemon registry of declared keys. Cached server-side. */
  async schema(): Promise<SettingsSchemaResponse> {
    return this.http.request(SettingsSchemaRoute)
  }

  /** Current value of every declared key. */
  async values(): Promise<SettingsValuesResponse> {
    return this.http.request(SettingsValuesRoute)
  }

  /** Full history of one daemon-scoped key, newest first. */
  async history(daemon: string, key: string): Promise<SettingsHistoryResponse> {
    return this.http.request(SettingsHistoryRoute, {
      params: { daemon, key },
    })
  }

  /**
   * Write a new value for one daemon-scoped key. The admin daemon
   * extracts `setBy` from the operator session — clients don't supply
   * it. Returns the freshly-inserted history row so callers can
   * re-render without a follow-up GET.
   */
  async set(
    daemon: string,
    key: string,
    value: unknown,
  ): Promise<SetSettingResponse> {
    return this.http.request(SetSettingRoute, {
      params: { daemon, key },
      body: { value },
    })
  }
}
